import { SingleSelectField } from '@/components/common/form/SingleSelectField';
import { MultiSelectField } from '@/components/common/form/MultiSelectField';
import { ConditionalSelectField } from '@/components/common/form/ConditionalSelectField';
import { resolveField, type QuestionDef, type AnswerValue, type ResolvedField } from '../types';

interface QuestionRendererProps {
  question: QuestionDef;
  answer: AnswerValue | undefined;
  setAnswer: (questionId: string, answer: AnswerValue) => void;
  isFirst: boolean;
}

/** question type ごとの description 表示位置 */
const DESCRIPTION_PLACEMENT: Record<QuestionDef['type'], 'inline' | 'block'> = {
  MULTI_SELECT: 'inline',
  SINGLE_SELECT: 'block',
  CONDITIONAL_SELECT: 'block',
};

/** description 表示：placement に応じて inline (h2 内 span) または block (p タグ) で描画 */
function renderQuestionDescription(
  question: QuestionDef,
  isFirst: boolean,
): { inlineDescription: React.ReactNode; blockDescription: React.ReactNode } {
  if (!question.description) {
    return { inlineDescription: null, blockDescription: null };
  }

  const placement = DESCRIPTION_PLACEMENT[question.type];

  if (placement === 'inline') {
    return {
      inlineDescription: (
        <span className="text-[#6B7280] text-sm ml-2">
          {question.description}
        </span>
      ),
      blockDescription: null,
    };
  }

  return {
    inlineDescription: null,
    blockDescription: (
      <p className={`text-[#6B7280] text-sm ${isFirst ? 'mb-4' : 'mb-3'}`}>
        {question.description}
      </p>
    ),
  };
}

/** ResolvedField の discriminated union を switch して各フィールドを描画 */
function renderField(
  resolved: ResolvedField,
  setAnswer: (questionId: string, answer: AnswerValue) => void,
): React.ReactNode {
  switch (resolved.type) {
    case 'SINGLE_SELECT':
      return (
        <SingleSelectField
          question={resolved.question}
          value={resolved.selectedId}
          onChange={(selectedId) =>
            setAnswer(resolved.question.id, { selectedId })
          }
        />
      );
    case 'MULTI_SELECT':
      return (
        <MultiSelectField
          question={resolved.question}
          value={resolved.selectedIds}
          onChange={(selectedIds) =>
            setAnswer(resolved.question.id, { selectedIds })
          }
        />
      );
    case 'CONDITIONAL_SELECT':
      return (
        <ConditionalSelectField
          question={resolved.question}
          selections={resolved.selections}
          secondarySelections={resolved.secondarySelections}
          onChange={(newSelections, newSecondarySelections) =>
            setAnswer(resolved.question.id, {
              selections: newSelections,
              secondarySelections: newSecondarySelections,
            })
          }
        />
      );
    default: {
      const _exhaustive: never = resolved;
      return _exhaustive;
    }
  }
}

/**
 * 個別の質問をタイプに応じてレンダリング
 */
export function QuestionRenderer({
  question,
  answer,
  setAnswer,
  isFirst,
}: QuestionRendererProps) {
  const { inlineDescription, blockDescription } = renderQuestionDescription(question, isFirst);

  return (
    <div className={isFirst ? '' : 'pt-4'}>
      <div className={isFirst ? 'mb-0' : ''}>
        <h2
          className={
            isFirst
              ? 'text-[#111827] mb-2 text-xl sm:text-2xl font-bold'
              : 'text-[#111827] mb-3 text-lg font-semibold'
          }
        >
          {question.label}
          {inlineDescription}
        </h2>
        {blockDescription}
      </div>

      {renderField(resolveField(question, answer), setAnswer)}
    </div>
  );
}
